import { cx } from '../lib/util.js';
import { useApp } from '../context/AppContext.jsx';

/* ------------------------------------------------------------------------ */
/* Shared UI primitives: buttons, inputs, labels, badges                     */
/* ------------------------------------------------------------------------ */

const BTN_SIZES = {
  xs: 'px-2 py-1 text-[10px]',
  sm: 'px-3 py-1.5 text-[11px]',
  md: 'px-4 py-2.5 text-xs',
  lg: 'px-6 py-3 text-sm',
};

const BTN_VARIANTS = {
  teal: 'bg-neon text-black font-bold hover:bg-[#6bffe9] shadow-neon-teal',
  'teal-outline': 'border border-neon/60 text-neon hover:bg-neon/10',
  ember: 'bg-ember text-white font-bold hover:bg-[#ff2b6e] shadow-neon-ember',
  'ember-outline': 'border border-ember/60 text-ember hover:bg-ember/10',
  ghost: 'border border-edge text-mist hover:border-raise hover:text-snow',
};

export function Btn({
  children,
  variant = 'teal',
  size = 'md',
  disabled,
  onClick,
  type = 'button',
  className,
  title,
  ...rest
}) {
  return (
    <button
      type={type}
      title={title}
      disabled={disabled}
      onClick={onClick}
      {...rest}
      className={cx(
        'inline-flex select-none items-center justify-center gap-2 font-mono uppercase tracking-widest transition-colors',
        BTN_SIZES[size],
        BTN_VARIANTS[variant],
        'disabled:cursor-not-allowed disabled:opacity-40',
        className
      )}
    >
      {children}
    </button>
  );
}

/* Same visual contract as Btn, but renders an anchor so external links stay
   real links (middle-click, new tab, copy address) instead of nested buttons. */
export function BtnLink({
  children,
  variant = 'teal',
  size = 'md',
  href,
  className,
  title,
  ...rest
}) {
  return (
    <a
      href={href}
      title={title}
      {...rest}
      className={cx(
        'inline-flex select-none items-center justify-center gap-2 font-mono uppercase tracking-widest transition-colors',
        BTN_SIZES[size],
        BTN_VARIANTS[variant],
        className
      )}
    >
      {children}
    </a>
  );
}

export function Field({ label, hint, children, className }) {
  return (
    <label className={cx('block', className)}>
      <span className="mb-1.5 block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-mist">
        {label}
      </span>
      {children}
      {hint && <span className="mt-1 block text-[11px] leading-relaxed text-dim">{hint}</span>}
    </label>
  );
}

export function MonoInput(props) {
  const { className, ...rest } = props;
  return (
    <input
      {...rest}
      className={cx(
        'w-full border border-edge bg-void px-3 py-2.5 font-mono text-sm text-snow outline-none transition-colors',
        'placeholder:text-dim',
        'focus:border-neon focus:shadow-neon-teal',
        className
      )}
    />
  );
}

export function Tag({ children, tone = 'teal', className }) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 border px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em]',
        tone === 'ember' ? 'border-ember/50 text-ember' : 'border-neon/50 text-neon',
        className
      )}
    >
      <span className={cx('h-1.5 w-1.5 rounded-full bg-current', tone !== 'ember' && 'animate-pulse')} />
      {children}
    </span>
  );
}

export function LiveBadge({ lastSync: propLastSync }) {
  const { lastSync: ctxLastSync } = useApp();
  const lastSync = propLastSync ?? ctxLastSync;
  const fresh = Boolean(lastSync) && Date.now() - lastSync.getTime() < 12000;
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 border border-neon/50 px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.3em]',
        fresh ? 'text-neon' : 'text-ember'
      )}
      title={fresh ? 'Live — all sessions in sync' : 'Last sync is stale'}
    >
      <span className={cx('h-1.5 w-1.5 rounded-full bg-current', fresh && 'animate-pulse')} />
      Live
    </span>
  );
}

export function PageHeading({ eyebrow, title, actions, live }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        {eyebrow && (
          <p className="mb-1 font-mono text-[10px] font-bold uppercase tracking-[0.35em] text-neon">
            {eyebrow}
          </p>
        )}
        <h1 className="font-mono text-2xl font-bold uppercase tracking-[0.2em] text-snow md:text-3xl">
          {title}
        </h1>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {live && <LiveBadge />}
        {actions}
      </div>
    </div>
  );
}